import { PATIENT_PATHS } from '@/utilities/paths'

/** Session storage key for the address-correction secret. The secret never goes in `localStorage` or cookies. */
export const ADDRESS_CORRECTION_SECRET_STORAGE_KEY = 'ash.address-correction.secret'

const FIX_DATA_PATH = PATIENT_PATHS.fixData
const FIX_DATA_SECRET_PREFIX = `${FIX_DATA_PATH}/`
// A secret is one non-empty path segment with no whitespace and no slash, after decoding.
const SECRET_PATTERN = /^[^\s/]+$/

// Holds the secret only when sessionStorage throws, so the page can still read it after the URL is clean.
let secretWhenStorageIsBlocked: string | null = null

function writeStoredSecret(secret: string): void {
  try {
    window.sessionStorage.setItem(ADDRESS_CORRECTION_SECRET_STORAGE_KEY, secret)
  } catch {
    secretWhenStorageIsBlocked = secret
  }
}

/** Removes the stored secret once the link is used or no longer valid, so page scripts cannot read it. */
export function clearStoredSecret(): void {
  secretWhenStorageIsBlocked = null
  try {
    window.sessionStorage.removeItem(ADDRESS_CORRECTION_SECRET_STORAGE_KEY)
  } catch {
    // Storage is blocked, so clearing the in-memory copy above is enough.
  }
}

function readStoredSecret(): string | null {
  try {
    const stored = window.sessionStorage.getItem(ADDRESS_CORRECTION_SECRET_STORAGE_KEY)?.trim()
    return stored || secretWhenStorageIsBlocked
  } catch {
    return secretWhenStorageIsBlocked
  }
}

/** Returns '' when the segment cannot be a secret, such as a blank segment or one with an encoded slash. */
function secretFromSegment(segment: string): string {
  let decoded: string
  try {
    decoded = decodeURIComponent(segment)
  } catch {
    return ''
  }
  return SECRET_PATTERN.test(decoded) ? decoded : ''
}

/**
 * Moves `/fix-data/{secret}` into session storage and removes the whole fix-data path from the address bar.
 * A malformed link clears any stored secret and returns null. Later calls read the stored value.
 * Call this before PostHog, Localize, or Zendesk load.
 */
export function takeAddressCorrectionSecret(): string | null {
  if (typeof window === 'undefined') return null

  const url = new URL(window.location.href)
  if (
    url.pathname.startsWith(FIX_DATA_SECRET_PREFIX) &&
    url.pathname.length > FIX_DATA_SECRET_PREFIX.length
  ) {
    const firstSegment = url.pathname.slice(FIX_DATA_SECRET_PREFIX.length).split('/')[0] ?? ''
    const secret = secretFromSegment(firstSegment)
    window.history.replaceState(
      window.history.state,
      '',
      `${FIX_DATA_PATH}${url.search}${url.hash}`,
    )
    if (secret) {
      writeStoredSecret(secret)
      return secret
    }
    clearStoredSecret()
    return null
  }

  return readStoredSecret()
}
